/**
 * Chooses between the legacy browser-side Toast fetch and the Lambda-backed one,
 * and provides the compare mode used to prove the two agree.
 *
 * @module clients/grm/views/R0002/ToastData/getToastDataDispatch
 */

import getToastData from './getToastData'
import getToastDataV2, { serializeStoreMap } from './getToastDataV2'
import { getToastPath, TOAST_PATH } from './toastPath'
import { fetchToastData } from './fetchToastData'
import diffToastPages from './diffToastPages'

/**
 * Run the legacy path for both periods, sequentially, exactly as R0002Main did.
 *
 * @param {string} curSDate
 * @param {string} curEDate
 * @param {string} compSDate
 * @param {string} compEDate
 * @param {Map<string, Object>} storeMap
 * @param {string} shiftString
 * @returns {Promise<{toastCur: Array, toastComp: Array}>}
 */
async function runLegacy(curSDate, curEDate, compSDate, compEDate, storeMap, shiftString) {
  const toastCur = await getToastData(curSDate, curEDate, storeMap, shiftString)
  const toastComp = await getToastData(compSDate, compEDate, storeMap, shiftString)
  return { toastCur, toastComp }
}

/**
 * Run both paths, diff them, and publish the result for inspection.
 *
 * Also captures the raw inputs so a browser observation can be turned into an
 * offline Jest fixture — see the Lambda's __tests__/fixtures. The capture uses
 * the real Cognito session, the real Lambda, and the actual storeMap this
 * browser produced, which matters because useAreas derives `status: 'Closed'`
 * from `closeDate <= today` and is therefore time-dependent.
 *
 * Copy the result with:  copy(JSON.stringify(window.__r0002ToastDiff))
 *
 * @returns {Promise<{toastCur: Array, toastComp: Array}>} The LEGACY result, so
 *   compare mode never changes what the user sees.
 */
async function runCompare(curSDate, curEDate, compSDate, compEDate, storeMap, shiftString) {
  const capture = {
    ranges: { curSDate, curEDate, compSDate, compEDate },
    shiftString,
    stores: serializeStoreMap(storeMap)
  }

  // Raw rows for the current period, so the fixture can replay the fold offline.
  // This re-fetches what runLegacy will fetch again below, roughly doubling the
  // cost of a compare run. Acceptable: compare mode is a developer tool, never
  // reachable for normal users, and capturing the exact input rows is the whole
  // point of the offline fixture.
  let rawCurrentRows = null
  try {
    rawCurrentRows = await fetchToastData(curSDate, curEDate)
  } catch (err) {
    capture.rawRowsError = err.message
  }

  const legacy = await runLegacy(curSDate, curEDate, compSDate, compEDate, storeMap, shiftString)

  let lambda = null
  let lambdaError = null
  try {
    lambda = await getToastDataV2(curSDate, curEDate, compSDate, compEDate, storeMap, shiftString)
  } catch (err) {
    lambdaError = err.message
  }

  window.__r0002ToastDiff = {
    ...capture,
    rawCurrentRows,
    lambdaError,
    meta: lambda ? lambda.meta : null,
    legacy,
    lambda: lambda ? { toastCur: lambda.toastCur, toastComp: lambda.toastComp } : null,
    diff: lambda
      ? {
          current: diffToastPages(legacy.toastCur, lambda.toastCur),
          comparison: diffToastPages(legacy.toastComp, lambda.toastComp)
        }
      : null
  }

  const d = window.__r0002ToastDiff.diff
  if (d) {
    const total = ['current', 'comparison'].reduce(
      (n, k) => n + d[k].fieldDiffs.length + d[k].keySetDiffs.length
        + d[k].pageIdMismatch.length + d[k].storesOnlyInOld.length + d[k].storesOnlyInNew.length,
      0
    )
    if (total === 0) {
      console.log('[R0002 compare] IDENTICAL — legacy and lambda agree')
    } else {
      console.warn(`[R0002 compare] ${total} difference(s). See window.__r0002ToastDiff.diff`)
    }
  }

  // Always return legacy so compare mode is observation-only.
  return legacy
}

/**
 * Fetch Toast data for both reporting periods by the configured path.
 *
 * On any Lambda failure this falls back to the legacy path rather than failing
 * the report. That covers cold-start errors, timeouts and missing IAM — it does
 * NOT cover the Lambda succeeding with wrong data, which is what the golden diff
 * is for.
 *
 * @param {string} curSDate
 * @param {string} curEDate
 * @param {string} compSDate
 * @param {string} compEDate
 * @param {Map<string, Object>} storeMap
 * @param {string} shiftString
 * @returns {Promise<{toastCur: Array, toastComp: Array}>}
 */
export default async function getToastDataDispatch(
  curSDate, curEDate, compSDate, compEDate, storeMap, shiftString
) {
  const path = getToastPath()

  if (path === TOAST_PATH.COMPARE) {
    return runCompare(curSDate, curEDate, compSDate, compEDate, storeMap, shiftString)
  }

  if (path === TOAST_PATH.LAMBDA) {
    try {
      const { toastCur, toastComp } = await getToastDataV2(
        curSDate, curEDate, compSDate, compEDate, storeMap, shiftString
      )
      return { toastCur, toastComp }
    } catch (err) {
      console.warn('[R0002] Lambda Toast path failed, falling back to legacy', {
        error: err.message,
        ranges: { curSDate, curEDate, compSDate, compEDate },
        shiftString
      })
    }
  }

  return runLegacy(curSDate, curEDate, compSDate, compEDate, storeMap, shiftString)
}
